import type { CountryResource } from "../../country";
import type { ProductResource } from "../../product";
import type { ProductGroupResource } from "../../product-group";
import type { OwnerProfileResource } from "../../owner-profile";
import type { CustomerGroupResource } from "../../customer-group";
import type { OwnerResource } from "../../owner";
import type { MarketplaceResource } from "../../marketplace";
import type { ManufacturerResource } from "../../manufacturer";
import type { SupplierResource } from "../../supplier";
import type { DiscountResource } from "../../discount";
import type { VoucherSerieResource } from "../../voucher-serie";

export enum SumFilterModeEnum {
  HigherThanAvg = "higher_than_average",
  LowerThanAvg = "lower_than_average",
  Range = "range",
}

export enum SexEnum {
  Female = "female",
  Male = "male",
  Unknown = "unknown",
}

export enum DownloadCustomersOptionEnum {
  WithPhone = "with_phone",
  WithMobilePhone = "with_mobile_phone",
  WithMailingAddress = "with_mailing_address",
  WithEmail = "with_email",
  // All = "all",
}

export interface MarketingConditionResource {
  id: string;
  gid: string;
  marketing_company_id: string;
  is_excluded: boolean;
  has_purchases: boolean;
  period_from: null | string;
  period_to: null | string;
  sum_from: null | string;
  sum_to: null | string;
  is_sum_per_order: boolean;
  sum_filter_mode: SumFilterModeEnum;
  day_of_week: null | number;
  order_time_from: null | string;
  order_time_to: null | string;
  order_count_from: null | string;
  order_count_to: null | string;
  excluded_products?: ProductResource[];
  product_name: string | null;
  included_products?: ProductResource[];

  excluded_owner_profiles?: OwnerProfileResource[];
  included_owner_profiles?: OwnerProfileResource[];

  excluded_product_groups?: ProductGroupResource[];
  included_product_groups?: ProductGroupResource[];

  included_owners?: OwnerResource[];
  included_marketplaces?: MarketplaceResource[];
  included_manufacturers?: ManufacturerResource[];

  included_suppliers?: SupplierResource[];
}

export enum SendSmsStatusEnum {
  NotSent = "not_sent",
  Pending = "pending",
  Sent = "sent",
}

export enum SendWhatsAppStatusEnum {
  NotSent = "not_sent",
  Pending = "pending",
  Sent = "sent",
}

export enum SendEmailStatusEnum {
  NotSent = "not_sent",
  Pending = "pending",
  Sent = "sent",
}

export interface MarketingCompanyResource {
  id: string;
  gid: string;
  has_phone: null | boolean;
  has_email: null | boolean;
  has_mobile_phone: null | boolean;
  has_mailing_address: null | boolean;
  age_from: number | null;
  age_to: number | null;
  brevo_account_id: null | string;
  zips: string;
  finished_at: string | null;
  name: string;
  description: string;
  customers_count_snapshot: string | null;
  average_revenue_snapshot: string | null;
  mobile_phone_count_snapshot: string | null;
  mailing_address_count_snapshot: string | null;
  phone_count_snapshot: string | null;
  email_count_snapshot: string | null;
  average_orders_count_snapshot: string | null;
  customer_share_snapshot: string | null;
  demography_snapshot: {
    "-1": number;
    "<18": number;
    "18-30": number;
    "31-45": number;
    "46-60": number;
    "61+": number;
  } | null;
  computed_at: string | null;

  countries?: CountryResource[];
  customer_groups?: CustomerGroupResource[];
  marketing_conditions?: MarketingCondition[];
  marketing_companies?: MarketingCompanyResource[];
  excluded_marketing_companies?: MarketingCompanyResource[];

  sms_text: string;
  email_subject: string;
  email_text_html: string;
  use_raw_email_text_html: boolean;
  sex: null | SexEnum;
  country_calling_codes: null | string;

  send_sms_to_customers_without_valid_email: boolean;
  send_email_to_customers_without_valid_mobile_phone: boolean;

  marketing_company_stats?: MarketingCompanyStatResource[];
  marketing_company_sms_texts?: MarketingCompanySmsText[];

  downloaded_at: string | null;
  downloaded_with: DownloadCustomersOptionEnum[];

  notification_group?: NotificationGroupResource;

  brevo_marketing_companies?: BrevoMarketingCompanyResource[];
  products?: ProductResource[];
  product_groups?: ProductGroupResource[];
  discounts?: DiscountResource[];
  voucher_series?: VoucherSerieResource[];
  brevo_stats?: BrevoStatResource[];
  outcome?: MarketingCompanyOutcomeResource[];
  custom_channel_outcome?: MarketingCompanyCustomerChannelOutcomeResource[];

  failed_notification_messages_count?: number;
  sent_notification_messages_count?: number;
  delivered_notification_messages_count?: number;
  read_notification_messages_count?: number;
  processing_notification_messages_count?: number;
  pending_notification_messages_count?: number;
  accepted_notification_messages_count?: number;

  are_all_mobile_phone_numbers_local: boolean;

  only_transfer_emails: boolean;
}

export enum NotificationChannel {
  Sms = "sms",
  Email = "email",
  Viber = "viber",
}

export type MarketingCompanyOutcomeResource = {
  id: string;
  all_purchases_customers_count: string;
  all_purchases_customers_share: string;
  all_purchases_total_gross: string;

  targeted_purchases_customers_count: string;
  targeted_purchases_customers_share: string;
  targeted_purchases_total_gross: string;

  channel: NotificationChannel;
};

export type MarketingCompanyCustomerChannelOutcomeResource = {
  id: string;
  all_purchases_customers_count: string;
  all_purchases_customers_share: string;
  all_purchases_total_gross: string;

  targeted_purchases_customers_count: string;
  targeted_purchases_customers_share: string;
  targeted_purchases_total_gross: string;

  with_phone: boolean;
  with_mailing_address: boolean;
  with_email: boolean;
  with_mobile_phone: boolean;
};

export type BrevoStatResource = {
  id: string;
  email_count: number;
  whatsapp_count: number;
  marketing_company_id: string;
  brevo_account_id: string;
  brevo_account?: BrevoAccountResource;
  marketing_company?: MarketingCompanyResource;
};

export type MarketingCompanySmsText = {
  value: string;
  marketing_company_id: string;
};

export type BrevoAccountResource = {
  id: string;
  owner_id: string;
  folder_id: number;
  sender_name: string;
  sender_email: string;
  api_key: string;
  created_at: string;
  updated_at: string | null;
  owner: OwnerResource;
};

export enum BrevoCampaignStatusEnum {
  Pending = "pending",
  Processed = "processed",
  Failure = "failure",
}

export type BrevoMarketingCompanyResource = {
  id: string;
  brevo_account_id: string;
  brevo_list_id: number;
  brevo_email_campaign_id: null | number;
  brevo_email_campaign_status: null | BrevoCampaignStatusEnum;
  created_at: string | null;
  updated_at: string | null;
  brevo_account?: BrevoAccountResource;
  sent_count: string;
  delivered_count: string;
  opened_count: string;
  link_clicked_count: string;
  error_count: string;
  spam_complaint_count: string;
  unsubscribed_count: string;
};

export type NotificationGroupResource = {
  finished_at: string | null;
  updated_at: string | null;
  created_at: string | null;
  title: string;
  id: string;
  gid: string;
};

export type MarketingCompanyStatResource = {
  owner_id: string;
  owner?: OwnerResource;
  marketing_company_id: string;
  marketing_company: MarketingCompanyResource;
  valid_email_count: number;
};

// for backward compatibility
export type MarketingCondition = MarketingConditionResource;
